/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    XMarkIcon,
    UserPlusIcon,
    EnvelopeIcon,
    ShieldCheckIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { projectService } from '../../services/api';
import { ProjectRole } from '../../types';

interface InviteMemberModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectId: string;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
    isOpen,
    onClose,
    projectId,
}) => {
    const queryClient = useQueryClient();
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<ProjectRole>('project_user');
    const [customPermissions, setCustomPermissions] = useState(false);
    const [permissions, setPermissions] = useState({
        can_create: false,
        can_read: true,
        can_update: false,
        can_delete: false,
    });

    const inviteMutation = useMutation({
        mutationFn: () => {
            const data: any = {
                email,
                role,
            };
            if (customPermissions) {
                data.can_create = permissions.can_create;
                data.can_read = permissions.can_read;
                data.can_update = permissions.can_update;
                data.can_delete = permissions.can_delete;
            }
            return projectService.inviteMember(projectId, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] });
            toast.success('Invitation sent successfully');
            handleClose();
        },
        onError: (error: any) => {
            toast.error(error.response?.data?.error || 'Failed to send invitation');
        },
    });

    const handleClose = () => {
        setEmail('');
        setRole('project_user');
        setCustomPermissions(false);
        setPermissions({
            can_create: false,
            can_read: true,
            can_update: false,
            can_delete: false,
        });
        onClose();
    };

    const handleRoleChange = (newRole: ProjectRole) => {
        setRole(newRole);
        if (newRole === 'project_admin') {
            setPermissions({
                can_create: true,
                can_read: true,
                can_update: true,
                can_delete: true,
            });
        } else {
            setPermissions({
                can_create: false,
                can_read: true,
                can_update: false,
                can_delete: false,
            });
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Email is required');
            return;
        }
        inviteMutation.mutate();
    };

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary-500/20 rounded-lg">
                            <UserPlusIcon className="w-6 h-6 text-primary-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">Invite Team Member</h3>
                            <p className="text-sm text-gray-400">Send an invitation to collaborate</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 text-gray-600 hover:text-gray-400 hover:bg-gray-800 rounded-lg"
                    >
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Email Address
                        </label>
                        <div className="relative">
                            <EnvelopeIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input pl-10"
                                placeholder="colleague@example.com"
                                required
                            />
                        </div>
                    </div>

                    {/* Role Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Role
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => handleRoleChange('project_user')}
                                className={`p-4 border-2 rounded-xl text-left transition-colors ${
                                    role === 'project_user'
                                        ? 'border-primary-500 bg-primary-500/10'
                                        : 'border-gray-700 hover:border-gray-600'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <EyeIcon className="w-5 h-5 text-blue-400" />
                                    <span className="font-medium text-white">Member</span>
                                </div>
                                <p className="text-xs text-gray-400">View-only access to resources</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRoleChange('project_admin')}
                                className={`p-4 border-2 rounded-xl text-left transition-colors ${
                                    role === 'project_admin'
                                        ? 'border-primary-500 bg-primary-500/10'
                                        : 'border-gray-700 hover:border-gray-600'
                                }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <ShieldCheckIcon className="w-5 h-5 text-purple-400" />
                                    <span className="font-medium text-white">Admin</span>
                                </div>
                                <p className="text-xs text-gray-400">Full access to manage resources</p>
                            </button>
                        </div>
                    </div>

                    {/* Custom Permissions Toggle */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-300">Custom Permissions</p>
                            <p className="text-xs text-gray-400">Override default role permissions</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={customPermissions}
                                onChange={(e) => setCustomPermissions(e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-500/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-gray-400 after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                        </label>
                    </div>

                    {/* Permission Checkboxes */}
                    {customPermissions && (
                        <div className="p-4 bg-gray-800 rounded-lg space-y-3">
                            <p className="text-sm font-medium text-gray-300 mb-2">Permissions</p>
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { key: 'can_create', label: 'Create', desc: 'Create new resources' },
                                    { key: 'can_read', label: 'Read', desc: 'View resources' },
                                    { key: 'can_update', label: 'Update', desc: 'Modify resources' },
                                    { key: 'can_delete', label: 'Delete', desc: 'Remove resources' },
                                ].map((perm) => (
                                    <label
                                        key={perm.key}
                                        className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-700 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={permissions[perm.key as keyof typeof permissions]}
                                            onChange={(e) =>
                                                setPermissions((prev) => ({
                                                    ...prev,
                                                    [perm.key]: e.target.checked,
                                                }))
                                            }
                                            className="mt-0.5 w-4 h-4 text-primary-500 rounded focus:ring-primary-500 bg-gray-800 border-gray-600"
                                        />
                                        <div>
                                            <p className="text-sm font-medium text-white">{perm.label}</p>
                                            <p className="text-xs text-gray-400">{perm.desc}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={inviteMutation.isPending}
                            className="flex-1 btn-primary disabled:opacity-50"
                        >
                            {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default InviteMemberModal;
